import { useCallback, useId, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IProjectMember } from "@/pages/Projects/types";
import { UserRole } from "@/types/users";
import type { ISelectOption } from "@/ui-kit";
import { ComboBox, FormField } from "@/ui-kit";
import type { IUserControlRole } from "./useUserOptions";
import { useUserOptions } from "./useUserOptions";

export type { IUserControlRole } from "./useUserOptions";

type IProps = {
  name: string;
  role: IUserControlRole;
  required?: boolean;
};

type IRoleText = {
  label: string;
  placeholder: string;
};

// Full keys per role: composing "Select {{what}}" breaks grammar in other locales
const ROLE_TEXT: Record<IUserControlRole, IRoleText> = {
  [UserRole.Admin]: {
    label: "label.admin",
    placeholder: "placeholder.selectAdmin",
  },
  [UserRole.Client]: {
    label: "label.client",
    placeholder: "placeholder.selectClient",
  },
  [UserRole.Contractor]: {
    label: "label.contractor",
    placeholder: "placeholder.selectContractor",
  },
  [UserRole.Inspector]: {
    label: "label.inspector",
    placeholder: "placeholder.selectInspector",
  },
  [UserRole.LoanOfficer]: {
    label: "label.loan_officer",
    placeholder: "placeholder.selectLoanOfficer",
  },
};

/** Picks one user of a role. The form value is `IProjectMember[]`: a project has one user per role. */
export const UserControl = (props: IProps) => {
  // A new role is a new list: remount so no state of the old role survives.
  return <UserField key={props.role} {...props} />;
};

const UserField = ({ name, role, required }: IProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const {
    field: { value: members, onChange: onFieldChange },
    fieldState: { error },
  } = useController({ name });
  const { options, loading, loadingMore, search, loadMore } =
    useUserOptions(role);

  const member = (members as IProjectMember[] | undefined)?.[0];
  const value = useMemo((): ISelectOption | undefined => {
    return member ? { id: member.id, text: member.fullName } : undefined;
  }, [member]);

  const onChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const selected = option as ISelectOption | undefined;
      onFieldChange(selected ? [selected.data as IProjectMember] : []);
    },
    [onFieldChange],
  );

  // ComboBox puts the picked name into its search box. Searching for it would
  // narrow the list to one user, so it reopens with the full list instead.
  const onSearch = useCallback(
    (query: string) => {
      const picked = (getValues(name) as IProjectMember[] | undefined)?.[0];
      search(query === picked?.fullName ? "" : query);
    },
    [getValues, name, search],
  );

  const onClear = useCallback(() => {
    search("");
  }, [search]);

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();
  const text = ROLE_TEXT[role];

  return (
    <FormField
      htmlFor={inputId}
      label={t(text.label)}
      message={error?.message}
      required={required}
    >
      <ComboBox
        inputId={inputId}
        options={options}
        value={value}
        onChange={onChange}
        onClear={onClear}
        onSearch={onSearch}
        onLoad={loadMore}
        loading={loading}
        loadingMore={loadingMore}
        placeholder={t(text.placeholder)}
        invalid={!!error}
        maxHeight={240}
        openLength={0}
      />
    </FormField>
  );
};
